import { type DehydratedState, dehydrate, hydrate, type QueryClient } from '@tanstack/react-query';
import type { Session } from '../auth/session';
import { tokenClaims } from '../auth/token-claims';
import type { OutboxStorage } from '../content/outbox';
import { hasUsableData, isAccessRefused } from '../domain/access-refusal';
import { loadedAtStore } from '../react/loaded-at-store';
import { createQueryClient } from './client';

/** Matches the query `gcTime`. */
const MAX_AGE_MS = 24 * 60 * 60_000;
const SAVE_THROTTLE_MS = 1_000;

interface PersistedCache {
  build: string;
  savedAt: number;
  /** The `sub` claim of the account the data was fetched for. */
  subject: string;
  /** When a request last succeeded (`loadedAtStore`), for the offline banner. */
  loadedAt?: number;
  state: DehydratedState;
}

const MAP_TAG = '__map';

/** JSON has no Map; `SavedShelf.saved` is one. */
function replacer(_key: string, value: unknown): unknown {
  return value instanceof Map ? { [MAP_TAG]: [...value] } : value;
}

function reviver(_key: string, value: unknown): unknown {
  return typeof value === 'object' && value !== null && MAP_TAG in value
    ? new Map((value as Record<string, [unknown, unknown][]>)[MAP_TAG])
    : value;
}

let storage: OutboxStorage | undefined;

/** Where the query cache is kept between launches; nothing is kept until this is called. */
export function configureQueryCache(adapter: OutboxStorage): void {
  storage = adapter;
}

function subjectOf(session: Session): string | undefined {
  const idToken = session.tokens?.idToken;
  const sub = idToken ? tokenClaims(idToken)?.sub : undefined;
  return typeof sub === 'string' && sub !== '' ? sub : undefined;
}

function read(build: string, now: number): PersistedCache | undefined {
  try {
    const raw = storage?.load();
    if (!raw) return undefined;
    const saved = JSON.parse(raw, reviver) as PersistedCache;
    return saved.build === build && now - saved.savedAt < MAX_AGE_MS && saved.subject
      ? saved
      : undefined;
  } catch {
    return undefined;
  }
}

export interface CachedQueryClient {
  client: QueryClient;
  /** Call with every session before anything renders from it. */
  observe(session: Session): void;
  /** Starts saving the cache as it changes; returns the stop function. */
  start(): () => void;
}

/**
 * A QueryClient seeded from the persisted cache. The seed belongs to whoever was signed in when
 * it was saved: it is dropped when the session ends, is for a different account, or carries no
 * identity to compare (data fetched this session is kept without one), and nothing is saved until the session is known to be that account's.
 * `build` ties the file to the JS build that wrote it.
 */
export function createCachedQueryClient(
  build: string,
  session: Session,
  now: () => number = Date.now,
): CachedQueryClient {
  const client = createQueryClient();
  const restored = read(build, now());
  const loaded = loadedAtStore(client.getQueryCache());
  if (restored) {
    hydrate(client, restored.state);
    // Restored data is shown as it was last loaded; each query refetches when next used.
    void client.invalidateQueries({ refetchType: 'none' });
    loaded.seed(restored.loadedAt);
  }
  let subject = restored?.subject;
  let verified = false;
  // The restored data has not yet been matched to an account.
  let seeded = restored !== undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const wipe = () => {
    clearTimeout(timer);
    timer = undefined;
    storage?.save('');
  };
  // Takes a refused query out of the file at once; `flush` may be deferred behind a mutation.
  const forget = (queryHash: string) => {
    try {
      const raw = storage?.load();
      if (!raw) return;
      const saved = JSON.parse(raw, reviver) as PersistedCache;
      saved.state.queries = saved.state.queries.filter((query) => query.queryHash !== queryHash);
      storage?.save(JSON.stringify(saved, replacer));
    } catch {
      wipe();
    }
  };
  const flush = () => {
    timer = undefined;
    if (!verified || !subject) return;
    // Optimistic updates are unconfirmed and the mutation itself is not saved.
    if (client.isMutating() > 0) {
      timer = setTimeout(flush, SAVE_THROTTLE_MS);
      return;
    }
    try {
      const state = dehydrate(client, {
        shouldDehydrateQuery: (query) =>
          hasUsableData(query.state) && query.queryKey[0] !== 'group-chat',
        shouldDehydrateMutation: () => false,
      });
      // A query holding data from before a failed refresh comes back as plain success.
      for (const query of state.queries) {
        query.state = { ...query.state, status: 'success', error: null, fetchStatus: 'idle' };
      }
      const saved: PersistedCache = {
        build,
        savedAt: now(),
        subject,
        loadedAt: loaded.get(),
        state,
      };
      storage?.save(JSON.stringify(saved, replacer));
    } catch {
      // Kept in memory; the next change saves again.
    }
  };

  const observe = (next: Session) => {
    if (next.status === 'unknown') return;
    if (next.status !== 'authenticated') {
      client.clear();
      wipe();
      subject = undefined;
      verified = false;
      seeded = false;
      return;
    }
    const current = subjectOf(next);
    if (!current) {
      if (seeded) {
        client.clear();
        wipe();
        subject = undefined;
      }
      seeded = false;
      return;
    }
    if (subject && subject !== current) {
      client.clear();
      wipe();
    }
    seeded = false;
    verified = true;
    subject = current;
  };
  observe(session);

  return {
    client,
    observe,
    start: () => {
      const unsubscribe = client.getQueryCache().subscribe((event) => {
        if (event.type === 'updated' && event.action.type === 'error') {
          if (isAccessRefused(event.action.error)) forget(event.query.queryHash);
        }
        if (event.type !== 'added' && event.type !== 'removed' && event.type !== 'updated') return;
        timer ??= setTimeout(flush, SAVE_THROTTLE_MS);
      });
      return () => {
        unsubscribe();
        clearTimeout(timer);
        timer = undefined;
      };
    },
  };
}
